import { useState } from "react";
import axios from "axios";
import Seo from "@/components/Seo";
import Logo from "@/components/landing/Logo";
import ConversationsPanel from "@/components/admin/ConversationsPanel";
import { Gamepad2, ArrowRight, Loader2, RotateCcw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  "Simulate a missed call",
  "Open it — reply as the homeowner",
  "Reply as the office, mark it booked",
];

export default function Playground() {
  const [company, setCompany] = useState("");
  const [session, setSession] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [followEmail, setFollowEmail] = useState("");
  const [followState, setFollowState] = useState(null); // null | sending | sent | error

  const sendFollowup = async (e) => {
    e.preventDefault();
    if (!followEmail.trim() || followState === "sending") return;
    setFollowState("sending");
    try {
      await axios.post(`${API}/playground/followup`, {
        email: followEmail.trim(),
        company_name: session?.company_name || company,
      });
      setFollowState("sent");
    } catch (err) {
      setFollowState("error");
    }
  };

  const start = async (e) => {
    e?.preventDefault();
    setStarting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/playground/session`, { company_name: company });
      setSession(data);
    } catch (err) {
      setError(typeof err.response?.data?.detail === "string" ? err.response.data.detail : "Couldn't start - try again.");
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0D10]">
      <Seo
        title="Interactive Playground — Drive Backfill Yourself"
        description="No signup, no sales call. Simulate a missed call, reply as the homeowner, and book the job — the real Backfill dashboard in a self-destructing sandbox."
      />
      <header className="border-b border-white/[0.08] bg-[#0B0D10]/75 backdrop-blur-xl">
        <div className="mx-auto flex h-[68px] max-w-7xl items-center justify-between px-5 sm:px-8">
          <a href="/" className="flex items-center gap-2.5" data-testid="playground-home-link">
            <Logo size={30} />
            <span className="font-display text-lg font-bold tracking-tight text-slate-50">Backfill</span>
            <span className="hidden rounded-full border border-[#F25F22]/40 px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-[#F25F22] sm:block">
              Playground
            </span>
          </a>
          <a
            href="/signup"
            data-testid="playground-signup-cta"
            className="group flex items-center gap-1.5 rounded-md bg-[#F25F22] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#FF7539]"
          >
            Start Now
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        {!session ? (
          <div data-testid="playground-start" className="mx-auto max-w-xl pt-10 text-center">
            <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border border-[#F25F22]/30 bg-[#F25F22]/10">
              <Gamepad2 className="h-7 w-7 text-[#F25F22]" />
            </div>
            <h1 className="mt-6 font-display text-3xl font-bold tracking-tight text-slate-50 sm:text-4xl">
              Don't watch the demo. <span className="text-[#F25F22]">Drive it.</span>
            </h1>
            <p className="mt-4 text-base leading-relaxed text-slate-400">
              This is the real Backfill dashboard in a private sandbox — your company name,
              your test calls, your clicks. No signup, no sales call. Everything you make
              here deletes itself in a few hours.
            </p>
            <form onSubmit={start} className="mt-8">
              <input
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                data-testid="playground-company-input"
                placeholder="Your company name (e.g. Torres HVAC)"
                maxLength={80}
                className="w-full rounded-md border border-white/[0.1] bg-[#12161F] px-4 py-3 text-center text-sm text-slate-100 placeholder:text-slate-600 outline-none focus:border-[#F25F22]/60 focus:ring-2 focus:ring-[#F25F22]/20"
              />
              <button
                type="submit"
                disabled={starting}
                data-testid="playground-start-button"
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-md bg-[#F25F22] px-6 py-3.5 text-sm font-semibold text-white transition-all hover:bg-[#FF7539] hover:shadow-[0_0_32px_rgba(242,95,34,0.45)] disabled:opacity-60"
              >
                {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gamepad2 className="h-4 w-4" />}
                {starting ? "Building your sandbox…" : "Start playing — free, no signup"}
              </button>
              {error && <p className="mt-3 text-sm text-red-400" data-testid="playground-error">{error}</p>}
            </form>
            <div className="mt-8 flex flex-wrap justify-center gap-2">
              {STEPS.map((s, i) => (
                <span key={s} className="rounded-full border border-white/[0.08] bg-white/[0.03] px-3.5 py-1.5 font-mono text-[10px] uppercase tracking-wider text-slate-500">
                  {i + 1}. {s}
                </span>
              ))}
            </div>
            <div className="mx-auto mt-10 max-w-md">
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
                New here? 40-second narrated walkthrough
              </p>
              <div className="overflow-hidden rounded-xl border border-white/[0.1] shadow-[0_30px_60px_-20px_rgba(0,0,0,0.7)]">
                <video
                  controls
                  preload="metadata"
                  data-testid="playground-walkthrough-video"
                  className="w-full bg-black"
                >
                  <source src="/backfill-playground-tour.mp4" type="video/mp4" />
                  Your browser does not support the video tag.
                </video>
              </div>
            </div>
          </div>
        ) : (
          <div data-testid="playground-dashboard">
            <div className="mb-5 flex flex-wrap items-center gap-3 rounded-xl border border-[#F25F22]/25 bg-[#F25F22]/[0.06] px-5 py-3.5">
              <Gamepad2 className="h-4 w-4 shrink-0 text-[#F25F22]" />
              <p className="text-sm text-slate-300">
                <span className="font-semibold text-slate-100">{session.company_name}</span> — interactive demo.
                Try: simulate a missed call, open the thread, reply as the homeowner, then book it.
                <span className="text-slate-500"> Sandboxes self-destruct in a few hours.</span>
              </p>
              <button
                onClick={() => { setSession(null); setCompany(""); }}
                data-testid="playground-reset"
                className="ml-auto flex items-center gap-1.5 rounded-md border border-white/[0.12] px-3 py-1.5 text-xs font-medium text-slate-300 hover:text-slate-50"
              >
                <RotateCcw className="h-3.5 w-3.5" /> New sandbox
              </button>
            </div>
            <ConversationsPanel token={session.token} onUnreadChange={() => {}} />
            <div className="mt-8 rounded-xl border border-white/[0.08] bg-[#12161F] p-6 text-center">
              <p className="font-display text-lg font-bold text-slate-50">
                Imagine this running on your phones tonight.
              </p>
              <p className="mx-auto mt-2 max-w-md text-sm text-slate-400">
                The real thing answers actual customers and books real jobs for you. Live within 48 hours.
              </p>
              <a
                href="/signup"
                data-testid="playground-bottom-cta"
                className="mt-5 inline-flex items-center gap-2 rounded-md bg-[#F25F22] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#FF7539]"
              >
                Put it on my phones <ArrowRight className="h-4 w-4" />
              </a>
              {followState === "sent" ? (
                <p className="mt-4 text-sm text-emerald-400" data-testid="playground-followup-sent">
                  Recap on its way — check your inbox.
                </p>
              ) : (
                <form onSubmit={sendFollowup} className="mx-auto mt-5 flex max-w-sm items-center gap-2">
                  <input
                    type="email"
                    required
                    value={followEmail}
                    onChange={(e) => setFollowEmail(e.target.value)}
                    data-testid="playground-followup-email"
                    placeholder="Email me a recap of this"
                    className="w-full min-w-0 rounded-md border border-white/[0.1] bg-[#0B0D10] px-3.5 py-2.5 text-xs text-slate-100 placeholder:text-slate-600 outline-none focus:border-[#F25F22]/60"
                  />
                  <button
                    type="submit"
                    disabled={followState === "sending"}
                    data-testid="playground-followup-send"
                    className="shrink-0 rounded-md border border-white/[0.12] px-3.5 py-2.5 text-xs font-semibold text-slate-200 transition-colors hover:border-[#F25F22]/50"
                  >
                    {followState === "sending" ? "…" : "Send"}
                  </button>
                </form>
              )}
              {followState === "error" && (
                <p className="mt-2 text-xs text-red-400" data-testid="playground-followup-error">Couldn&apos;t send — try again.</p>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
